/// <amd-module name="Wit/Common/ColumnOptionsPanel/ColumnOptionsPanel" />
import * as React from "react";
import { useEffect, useRef } from "react";
import { IReadonlyObservableArray, ObservableArray, ObservableValue, useObservableArray } from "VSS/Core/Observable";
import { IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { EditableDropdown } from "VSSUI/EditableDropdown";
import { Icon } from "VSSUI/Icon";
import { BoltListDragEvent, IListDropData, ListDragDropBehavior } from "VSSUI/List";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { IMenuItem, MenuButton, MenuItemType } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { Tab, TabBar } from "VSSUI/Tabs";
import { HubType } from "Wit/Common/BacklogContentService/BacklogContentTreeTypes";
import { getRollupColumnReferenceName, IRollupColumn, RollupCalculationSettings, RollupType } from "Wit/Common/BacklogContentService/RollupHelpers";
import * as Resources from "Wit/Common/ColumnOptionsPanel/Resources";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { CustomRollupConfigurationPanel } from "./CustomRollupConfigurationPanel";
import { ICommonBacklogContentService } from "Wit/Common/BacklogContentService/ICommonBacklogContentService";

const enum ColumnOptionsTabs {
    Columns = "columns",
    Sorting = "sorting"
}

export interface IColumnOptionsPanelProps {
    availableRollupColumns?: IRollupColumn[];
    columnTableItems: ObservableArray<IColumnOptionTableItem>;
    dropdownItems: IListBoxItem<IFieldEntry>[];
    loading?: ObservableValue<boolean>;
    onDismiss: () => void;
    onSubmit: () => void;
    sortableDropdownItems?: IListBoxItem<IFieldEntry>[];
    sortTableItems?: ObservableArray<ISortColumnTableItem>;
    hubType?: HubType;
}

export interface IColumnOptionTableItem {
    selectedField?: ObservableValue<IFieldEntry | undefined>;
    required?: boolean;
    isRollupColumn?: boolean;
    rollupCalculation?: RollupCalculationSettings;
    isCustomRollup?: boolean;
    friendlyNames?: { backlogName?: string; fieldName?: string };
    autofocus?: boolean;
}

interface ISortColumnTableItem extends IColumnOptionTableItem {
    descending: ObservableValue<boolean>;
}

export class ColumnOptionsPanel extends VssComponent<IColumnOptionsPanelProps> {
    private selectedTabId = new ObservableValue<string>(ColumnOptionsTabs.Columns);
    private commonBacklogContentService = this.context.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");

    private renderColumnOption = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IColumnOptionTableItem>,
        columnOption: IColumnOptionTableItem
    ) => {
        if (columnOption.selectedField) {
            return (
                <ColumnOption
                    key={"sort-option" + rowIndex + columnOption.selectedField.value?.name}
                    rowIndex={rowIndex}
                    columnIndex={columnIndex}
                    tableItems={this.props.columnTableItems}
                    dropdownItems={this.props.dropdownItems}
                    selectedFieldObservable={columnOption.selectedField}
                    required={columnOption.required}
                    isOnlyItem={this.props.columnTableItems.length === 1}
                    autofocus={columnOption.autofocus}
                />
            );
        } else {
            return (
                <RollupColumnOption
                    key={"rollup-option" + rowIndex}
                    rowIndex={rowIndex}
                    columnIndex={columnIndex}
                    tableItems={this.props.columnTableItems}
                    rollupColumns={this.props.availableRollupColumns}
                    isCustomColumn={columnOption.isCustomRollup}
                    selectedText={new ObservableValue(this.commonBacklogContentService.getRollupColumnNameAlignedWithBacklog(columnOption.rollupCalculation!, columnOption.friendlyNames))}
                />
            );
        }
    };

    private renderSortOption = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IColumnOptionTableItem>,
        sortColumn: ISortColumnTableItem
    ) => {
        if (!sortColumn.selectedField) {
            // Sort columns will always have selected fields
            return <></>;
        }
        return (
            <ColumnOption
                key={"sort-option" + rowIndex + sortColumn.selectedField.value?.name}
                rowIndex={rowIndex}
                columnIndex={columnIndex}
                tableItems={this.props.sortTableItems!}
                dropdownItems={this.props.sortableDropdownItems!}
                selectedFieldObservable={sortColumn.selectedField}
                descendingObservable={sortColumn.descending}
                autofocus={sortColumn.autofocus}
            />
        );
    };

    private columns = [{ id: "option", width: -100, renderCell: this.renderColumnOption }];
    private sortColumns = [{ id: "sort-option", width: -100, renderCell: this.renderSortOption }];

    private hasEmptyValues = (columnTableItems: IColumnOptionTableItem[], sortTableItems?: IColumnOptionTableItem[]) => {
        return (
            !!columnTableItems.find(tableItem => tableItem.selectedField && !tableItem.selectedField.value) ||
            (sortTableItems && !!sortTableItems.find(tableItem => !tableItem.selectedField!.value))
        );
    };

    private hasDuplicates = (columnTableItems: IColumnOptionTableItem[], sortTableItems?: IColumnOptionTableItem[]) => {
        let tempCheckSet = new Set(columnTableItems.map(item => (item.rollupCalculation ?  getRollupColumnReferenceName(item.rollupCalculation) : item.selectedField?.value?.referenceName)));
        let hasDuplicates = tempCheckSet.size !== columnTableItems.length;
        if (!hasDuplicates && sortTableItems) {
            tempCheckSet = new Set(sortTableItems.map(item => (item.rollupCalculation ?  getRollupColumnReferenceName(item.rollupCalculation) : item.selectedField?.value?.referenceName)));
            hasDuplicates = tempCheckSet.size !== sortTableItems.length;
        }
        return hasDuplicates;
    };

    public render() {
        return (
            <Observer columnTableItems={this.props.columnTableItems} sortTableItems={this.props.sortTableItems}>
                {({
                    columnTableItems,
                    sortTableItems
                }: {
                    columnTableItems: IColumnOptionTableItem[];
                    sortTableItems?: IColumnOptionTableItem[];
                }) => {
                    let hasDuplicates = false;
                    const hasEmptyValues = this.hasEmptyValues(columnTableItems, sortTableItems);
                    if (!hasEmptyValues) {
                        hasDuplicates = this.hasDuplicates(columnTableItems, sortTableItems);
                    }
                    return (
                        <Panel
                            className="column-options-panel"
                            onDismiss={this.props.onDismiss}
                            titleProps={{ text: Resources.ColumnOptions }}
                            footerButtonProps={[
                                { text: Resources.Cancel, onClick: this.props.onDismiss },
                                {
                                    text: Resources.OK,
                                    primary: true,
                                    disabled: hasEmptyValues || hasDuplicates,
                                    onClick: this.props.onSubmit,
                                    tooltipProps: hasEmptyValues
                                        ? { text: Resources.EmptyColumnValueMessage }
                                        : hasDuplicates
                                        ? { text: Resources.DuplicateColumnValueMessage }
                                        : undefined
                                }
                            ]}
                        >
                            <div>
                                <div>{Resources.ColumnOptionsMessage}</div>
                                {hasDuplicates && <div className="margin-top-8 column-options-error-message">{Resources.DuplicateColumnValueMessage}</div>}
                                {this.props.sortTableItems && (
                                    <TabBar selectedTabId={this.selectedTabId} onSelectedTabChanged={this.onSelectedTabChanged}>
                                        <Tab name={Resources.Columns} id={ColumnOptionsTabs.Columns} />
                                        <Tab name={Resources.Sorting} id={ColumnOptionsTabs.Sorting} />
                                    </TabBar>
                                )}
                                <Observer loading={this.props.loading} rollupColumn={this.props.availableRollupColumns}>
                                    {({ loading, rollupColumn }: { loading: boolean | undefined; rollupColumn?: IRollupColumn[] }) =>
                                        loading ? (
                                            <Spinner label={Resources.Loading} />
                                        ) : (
                                            <Observer selectedTabId={this.selectedTabId}>
                                                {({ selectedTabId }: { selectedTabId: string }) => (
                                                    <>
                                                        <Table
                                                            className="column-options-table"
                                                            columns={selectedTabId === ColumnOptionsTabs.Columns ? this.columns : this.sortColumns}
                                                            behaviors={[this.dragDropBehavior]}
                                                            itemProvider={
                                                                selectedTabId === ColumnOptionsTabs.Columns
                                                                    ? this.props.columnTableItems
                                                                    : this.props.sortTableItems
                                                            }
                                                            spacerWidth={0}
                                                            showHeader={false}
                                                        />
                                                        <Button
                                                            className="add-column-button"
                                                            subtle={true}
                                                            iconProps={{ iconName: "Add" }}
                                                            text={Resources.AddColumn}
                                                            onClick={this.onAddColumn}
                                                        />
                                                        {this.props.hubType === HubType.Backlogs && (
                                                            <div className="flex-row">
                                                                <MenuButton
                                                                    className="add-column-button"
                                                                    iconProps={{ iconName: "Add" }}
                                                                    subtle={true}
                                                                    text={Resources.AddRollupColumn}
                                                                    contextualMenuProps={{
                                                                        menuProps: {
                                                                            id: "add-rollup-column",
                                                                            items: this.onAddRollupColumn(rollupColumn)
                                                                        }
                                                                    }}
                                                                />
                                                                <Button
                                                                    className="tooltip-button-margin-top-8"
                                                                    ariaLabel={Resources.RollupColumnTooltip}
                                                                    subtle={true}
                                                                    iconProps={{ iconName: "Info", className: "column-info" }}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </Observer>
                                        )
                                    }
                                </Observer>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private onAddColumn = () => {
        if (this.selectedTabId.value === ColumnOptionsTabs.Columns) {
            this.props.columnTableItems.push({ selectedField: new ObservableValue<IFieldEntry | undefined>(undefined), autofocus: true });
        } else {
            this.props.sortTableItems!.push({
                selectedField: new ObservableValue<IFieldEntry | undefined>(undefined),
                descending: new ObservableValue<boolean>(false),
                autofocus: true
            });
        }
    };

    private onAddRollupColumn(rollupColumns: IRollupColumn[] | undefined): IMenuItem[] {
        if (!rollupColumns) {
            return [];
        }
        const rollupMenuItems: IMenuItem[] = [];
        rollupMenuItems.push({
            text: Resources.ConfigureCustomRollup,
            onActivate: () => {
                this.renderCustomConfigPanel();
            },
            id: "custom-rollup"
        });
        if (rollupColumns && rollupColumns.length > 0) {
            rollupMenuItems.unshift({
                id: "column-options-panel-add-rollup-column-from-quicklist",
                text: Resources.AddARollupColumnFromQuickList,
                subMenuProps: {
                    id: "column-options-panel-rollup-columns-quicklist",
                    items: this._getQuicklistItemsWithHeaders(rollupColumns)
                }
            });
        }

        return rollupMenuItems;
    }

    private renderCustomConfigPanel() {
        const onSubmit = (rollupCalculation: RollupCalculationSettings, friendlyNames?: { backlogName?: string; fieldName?: string }) => {
            this.props.columnTableItems.push({
                isRollupColumn: true,
                rollupCalculation: rollupCalculation,
                isCustomRollup: true,
                friendlyNames: friendlyNames
            });
        };

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return <CustomRollupConfigurationPanel onDismiss={onDismiss} onSubmit={onSubmit} />;
        });
    }
    private _getQuicklistItemsWithHeaders(availableQuicklistItems: IRollupColumn[]): IMenuItem[] | IReadonlyObservableArray<IMenuItem> {
        const onSubmit = (rollupCalculation: RollupCalculationSettings) => {
            this.props.columnTableItems.push({
                isRollupColumn: true,
                rollupCalculation: rollupCalculation,
                isCustomRollup: false,
                friendlyNames: {
                    backlogName: rollupCalculation.backlogFilter,
                    fieldName: rollupCalculation.aggregationFieldDisplayName ?? rollupCalculation.aggregationField
                }
            });
        };

        const quicklistRollupItems: IMenuItem[] = availableQuicklistItems.map(rollupColumnField => {
            return {
                id: rollupColumnField.referenceName,
                text: rollupColumnField.displayName,
                onActivate: () => {
                    onSubmit(rollupColumnField.rollupCalculation);
                }
            };
        });
        if (
            availableQuicklistItems.some(
                availableQuicklistItem =>
                    availableQuicklistItem.rollupCalculation && availableQuicklistItem.rollupCalculation.type === RollupType.Progress
            )
        ) {
            quicklistRollupItems.unshift({
                id: "add-progress-rollup-column-heading",
                text: Resources.RollupProgressBarOptionsHeadingText,
                itemType: MenuItemType.Header
            });
        }
        for (let i = 0; i < quicklistRollupItems.length; i++) {
            const fieldRefName = quicklistRollupItems[i].id as string;
            if (fieldRefName.toLowerCase().indexOf("system.backlog.rollup.total") === 0) {
                quicklistRollupItems.splice(i, 0, {
                    id: "add-total-rollup-column-heading",
                    text: Resources.RollupTotalNumberOptionsHeadingText,
                    itemType: MenuItemType.Header
                });
                break;
            }
        }
        return quicklistRollupItems;
    }

    private onDrop = (event: BoltListDragEvent<HTMLElement, IColumnOptionTableItem>, dropData: IListDropData) => {
        if (this.selectedTabId.value === ColumnOptionsTabs.Columns) {
            this.handleDrop(event, dropData, this.props.columnTableItems);
        } else {
            this.handleDrop(event, dropData, this.props.sortTableItems!);
        }
    };

    private handleDrop = (
        event: BoltListDragEvent<HTMLElement, IColumnOptionTableItem>,
        dropData: IListDropData,
        tableItems: ObservableArray<IColumnOptionTableItem>
    ) => {
        const index = tableItems.value.indexOf(event.detail.dataTransfer.data);
        const movedItem = tableItems.splice(index, 1);
        const dropIndex = index < dropData.index ? dropData.index - 1 : dropData.index;
        tableItems.splice(dropIndex, 0, movedItem[0]);
    };

    private dragDropBehavior = new ListDragDropBehavior<IColumnOptionTableItem>({
        allowedTypes: ["column"],
        id: "table",
        onDrop: this.onDrop,
        renderDragImage: event => {
            const iconProps = event.detail.dataTransfer.data.required
                ? { iconName: "Info", className: "column-info" }
                : { iconName: "Cancel", className: "error-text-important" };
            return (
                <div className="flex-row flex-grow">
                    <EditableDropdown selectedText={event.detail.dataTransfer.data.selectedField!.value?.name} className="flex-grow" items={[]} />
                    <Button subtle={true} iconProps={iconProps} />
                </div>
            );
        },
        type: "column"
    });

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;
    };
}

interface IColumnOptionProps {
    rowIndex: number;
    columnIndex: number;
    tableItems: ObservableArray<IColumnOptionTableItem>;
    dropdownItems: IListBoxItem<IFieldEntry>[];
    selectedFieldObservable: ObservableValue<IFieldEntry | undefined>;
    descendingObservable?: ObservableValue<boolean>;
    required?: boolean;
    isOnlyItem?: boolean;
    autofocus?: boolean;
}

function getColumnOptionItems(
    dropdownItems: IListBoxItem<IFieldEntry>[],
    selectedField: IFieldEntry | undefined,
    tableItems: ObservableArray<IColumnOptionTableItem>,
    currentText?: string
): IListBoxItem<IFieldEntry>[] {
    return dropdownItems
        .filter(
            item =>
                item.id === selectedField?.id.toString() ||
                !tableItems.value.find(tableItem => {
                    if (tableItem.selectedField) {
                        tableItem.selectedField.value?.id.toString() === item.id;
                    }
                })
        )
        .sort((a, b) => {
            if (a.text?.toLowerCase() === currentText?.toLowerCase()) {
                return -1;
            } else if (b.text?.toLowerCase() === currentText?.toLowerCase()) {
                return 1;
            }
            return 0;
        });
}

function ColumnOption(props: IColumnOptionProps) {
    const { rowIndex, columnIndex, tableItems, dropdownItems, selectedFieldObservable, descendingObservable, required, isOnlyItem } = props;
    const [items] = useObservableArray(getColumnOptionItems(dropdownItems, selectedFieldObservable.value, tableItems, ""));
    const fieldDropdownRef = useRef<EditableDropdown<IFieldEntry> | null>(null);

    const autofocus = !!props.autofocus;
    useEffect(() => {
        if (autofocus) {
            fieldDropdownRef.current?.focus();
        }
    }, [autofocus]);

    return (
        <Observer
            selectedField={selectedFieldObservable}
            onUpdate={() => (items.value = getColumnOptionItems(dropdownItems, selectedFieldObservable.value, tableItems, ""))}
        >
            {({ selectedField }: { selectedField: undefined | IFieldEntry }) => (
                <SimpleTableCell columnIndex={columnIndex} contentClassName="column-options-cell">
                    <div className="flex-row flex-grow flex-center">
                        <Icon iconName="GripperDotsVertical" className="column-options-gripper bolt-table-cell-content-reveal" />
                        <Icon iconName="RowsChild" className="invisible padding-horizontal-4" />
                        <EditableDropdown
                            className="flex-grow icon-margin"
                            ref={fieldDropdownRef}
                            disabled={required}
                            items={items}
                            onTextChange={(e, text) =>
                                (items.value = getColumnOptionItems(dropdownItems, selectedFieldObservable.value, tableItems, text))
                            }
                            onValueChange={value => {
                                selectedFieldObservable.value = value?.data;
                                // force the okay button to re-evaluate whether it should be disabled.
                                tableItems.splice(rowIndex, 0);
                            }}
                            selectedText={selectedField?.name}
                        />
                        {descendingObservable && (
                            <Observer descending={descendingObservable}>
                                {({ descending }: { descending: boolean }) => (
                                    <Button
                                        ariaLabel={descending ? Resources.SortDescendingAriaLabel : Resources.SortAscendingAriaLabel}
                                        subtle={true}
                                        iconProps={{ iconName: descending ? "Down" : "Up" }}
                                        onClick={() => (descendingObservable.value = !descending)}
                                    />
                                )}
                            </Observer>
                        )}
                        {required || isOnlyItem ? (
                            <Button
                                ariaLabel={required ? Resources.RequiredColumn : Resources.OneColumnRequiredMessage}
                                subtle={true}
                                iconProps={{ iconName: "Info", className: "column-info" }}
                            />
                        ) : (
                            <Button
                                ariaLabel={Resources.RemoveColumn}
                                subtle={true}
                                iconProps={{ iconName: "Cancel", className: "error-text-important" }}
                                onClick={() => {
                                    tableItems.splice(rowIndex, 1);
                                }}
                            />
                        )}
                    </div>
                </SimpleTableCell>
            )}
        </Observer>
    );
}

interface IRollupColumnOptionProps {
    rowIndex: number;
    columnIndex: number;
    tableItems: ObservableArray<IColumnOptionTableItem>;
    selectedText: ObservableValue<string | undefined>;
    isCustomColumn?: boolean;
    rollupColumns: IRollupColumn[] | undefined;
}

function getQuicklistItemsWithHeaders(availableQuicklistItems: IRollupColumn[] | undefined): IListBoxItem<RollupCalculationSettings>[] {
    if (!availableQuicklistItems) {
        return [];
    }
    const quicklistRollupItems: IListBoxItem<RollupCalculationSettings>[] = availableQuicklistItems.map(rollupColumnField => {
        return {
            id: rollupColumnField.referenceName,
            text: rollupColumnField.displayName,
            data: rollupColumnField.rollupCalculation
        };
    });
    if (
        availableQuicklistItems.some(
            availableQuicklistItem =>
                availableQuicklistItem.rollupCalculation && availableQuicklistItem.rollupCalculation.type === RollupType.Progress
        )
    ) {
        quicklistRollupItems.unshift({
            id: "add-progress-rollup-column-heading",
            text: Resources.RollupProgressBarOptionsHeadingText,
            type: ListBoxItemType.Header
        });
    }
    for (let i = 0; i < quicklistRollupItems.length; i++) {
        const fieldRefName = quicklistRollupItems[i].id as string;
        if (fieldRefName.toLowerCase().indexOf("system.backlog.rollup.total") === 0) {
            quicklistRollupItems.splice(i, 0, {
                id: "add-total-rollup-column-heading",
                text: Resources.RollupTotalNumberOptionsHeadingText,
                type: ListBoxItemType.Header
            });
            break;
        }
    }
    return quicklistRollupItems;
}

function RollupColumnOption(props: IRollupColumnOptionProps) {
    const { rowIndex, columnIndex, tableItems, selectedText, isCustomColumn, rollupColumns } = props;
    const [items] = useObservableArray(getQuicklistItemsWithHeaders(rollupColumns));

    return (
        <Observer selectedField={selectedText} onUpdate={() => (items.value = getQuicklistItemsWithHeaders(rollupColumns))}>
            {({ selectedField }: { selectedField: undefined | string }) => (
                <SimpleTableCell columnIndex={columnIndex} contentClassName="column-options-cell">
                    <div className="flex-row flex-grow flex-center">
                        <Icon iconName="GripperDotsVertical" className="column-options-gripper bolt-table-cell-content-reveal" />
                        <Icon iconName="RowsChild" className="padding-horizontal-4" />
                        <EditableDropdown
                            className="flex-grow icon-margin"
                            disabled={isCustomColumn}
                            items={items}
                            onValueChange={(item: IListBoxItem<RollupCalculationSettings>) =>{
                                selectedText.value = item.text;
                                tableItems.splice(rowIndex, 1, {
                                    rollupCalculation: item.data,
                                    isRollupColumn: true,
                                    isCustomRollup: false
                                });
                            }}
                            selectedText={selectedField}
                        />
                        <Button
                            ariaLabel={Resources.RemoveColumn}
                            subtle={true}
                            iconProps={{ iconName: "Cancel", className: "error-text-important" }}
                            onClick={() => {
                                tableItems.splice(rowIndex, 1);
                            }}
                        />
                    </div>
                </SimpleTableCell>
            )}
        </Observer>
    );
}
